import { API_BASE_URL } from "../../config/apiConfig";
import { createAuthAxios, handleApiError } from "../../utils/apiUtils";

/**
 * Global Search Service for ResearchPro
 * 
 * Uses the NEW search endpoints from backend:
 * - GET /api/search/all          - Unified search (communities + threads)
 * - GET /api/search/threads      - Global thread search
 * - GET /api/search/communities  - Community search
 * - GET /api/communities/:id/threads?search=...  - Thread search within community
 * - GET /api/topics/search       - Topic autocomplete
 */
const searchService = {
  /**
   * 🔥 Unified search - communities AND threads in one call
   * This is the primary endpoint for the global search dropdown
   * 
   * @param {string} query - Search query
   * @param {number} limit - Results per type (default: 5)
   * @returns {Promise<Object>} { communities: [...], threads: [...], _highlights }
   */
  searchAll: async (query, limit = 5) => {
    try {
      if (!query || query.trim().length < 1) {
        return { 
          success: true, 
          data: { communities: [], threads: [] } 
        };
      }

      const api = createAuthAxios();
      const params = new URLSearchParams();
      params.append("query", query.trim());
      params.append("limit", limit.toString());

      const response = await api.get(
        `${API_BASE_URL}/search/all?${params.toString()}`
      );

      return response.data;
    } catch (error) {
      const errorData = handleApiError(error);
throw errorData;
    }
  },

  /**
   * Search threads globally across ALL communities
   * Full-text search on title and description with Elasticsearch
   * 
   * @param {Object} options - Search options
   * @param {string} options.query - Search query
   * @param {string[]} options.tags - Optional tag filters
   * @param {string} options.sortBy - Sort: 'relevance', 'hot', 'new', 'top'
   * @param {number} options.page - Page number
   * @param {number} options.limit - Results per page
   * @returns {Promise<Object>} { threads: [...], pagination: {...} }
   */
  searchThreads: async ({ 
    query = "", 
    tags = [], 
    sortBy = "relevance", 
    page = 1, 
    limit = 20 
  } = {}) => {
    try {
      if (!query || query.trim().length < 1) {
        return { 
          success: true, 
          data: { threads: [], pagination: { total: 0, totalPages: 0 } } 
        };
      }

      const api = createAuthAxios();
      const params = new URLSearchParams();
      params.append("query", query.trim());
      params.append("sortBy", sortBy);
      params.append("page", page.toString());
      params.append("limit", limit.toString());
      
      if (tags && tags.length > 0) {
        params.append("tags", tags.join(","));
      }

      const response = await api.get(
        `${API_BASE_URL}/search/threads?${params.toString()}`
      );

      return response.data;
    } catch (error) {
      const errorData = handleApiError(error);
throw errorData;
    }
  },

  /**
   * Search communities by name, description, field
   * Full-text search with Elasticsearch
   * 
   * @param {Object} options - Search options
   * @param {string} options.query - Search query
   * @param {string} options.field - Filter by research field
   * @param {string} options.sortBy - Sort: 'relevance', 'hot', 'new', 'top'
   * @param {number} options.page - Page number
   * @param {number} options.limit - Results per page
   * @returns {Promise<Object>} { communities: [...], pagination: {...} }
   */
  searchCommunities: async ({ 
    query = "", 
    field = "", 
    sortBy = "relevance", 
    page = 1, 
    limit = 20 
  } = {}) => {
    try {
      if (!query || query.trim().length < 1) {
        return { 
          success: true, 
          data: { communities: [], pagination: { total: 0, totalPages: 0 } } 
        };
      }

      const api = createAuthAxios();
      const params = new URLSearchParams();
      params.append("query", query.trim());
      params.append("sortBy", sortBy);
      params.append("page", page.toString());
      params.append("limit", limit.toString());
      
      if (field) {
        params.append("field", field);
      }

      const response = await api.get(
        `${API_BASE_URL}/search/communities?${params.toString()}`
      );

      return response.data;
    } catch (error) {
      const errorData = handleApiError(error);
throw errorData;
    }
  },

  /**
   * Search threads within a SPECIFIC community
   * 
   * @param {string} communityId - Community ID
   * @param {Object} options - Search options
   * @param {string} options.search - Search query
   * @param {string[]} options.tags - Tag filters
   * @param {string} options.sortBy - Sort: 'hot', 'new', 'top', 'rising'
   * @param {number} options.page - Page number
   * @returns {Promise<Object>} { threads: [...], pagination: {...} }
   */
  searchCommunityThreads: async (communityId, { 
    search = "", 
    tags = [], 
    sortBy = "hot", 
    page = 1 
  } = {}) => {
    try {
      if (!communityId) {
        throw new Error("Community ID is required");
      }

      const api = createAuthAxios();
      const params = new URLSearchParams();
      
      if (search && search.trim()) {
        params.append("search", search.trim());
      }
      
      if (tags && tags.length > 0) {
        params.append("tags", tags.join(","));
      }
      
      params.append("sortBy", sortBy);
      params.append("page", page.toString());

      const response = await api.get(
        `${API_BASE_URL}/communities/${communityId}/threads?${params.toString()}`
      );

      return response.data;
    } catch (error) {
      const errorData = handleApiError(error);
throw errorData;
    }
  },

  /**
   * Topic autocomplete search
   * 
   * @param {string} query - Topic query
   * @param {number} limit - Max results
   * @returns {Promise<Object>} { topics: [{ topic, postCount }] }
   */
  searchTopics: async (query, limit = 10) => {
    try {
      if (!query || query.trim().length < 1) {
        return { success: true, data: { topics: [] } };
      }

      const api = createAuthAxios();
      const params = new URLSearchParams();
      params.append("q", query.trim());
      params.append("limit", limit.toString());

      const response = await api.get(
        `${API_BASE_URL}/topics/search?${params.toString()}`
      );

      return response.data;
    } catch (error) {
      const errorData = handleApiError(error);
throw errorData;
    }
  },

  /**
   * Global search wrapper (for backwards compatibility)
   * Uses the unified /api/search/all endpoint
   * 
   * @param {string} query - Search query
   * @param {Object} options - Options
   * @returns {Promise<Object>} Combined results
   */
  globalSearch: async (query, options = {}) => {
    try {
      if (!query || query.trim().length < 2) {
        return {
          success: true,
          data: {
            communities: [],
            threads: [],
            query: query,
            totalResults: 0,
          },
        };
      }

      const { limit = 10 } = options;

      // Use the unified search endpoint
      const result = await searchService.searchAll(query, limit);

      const communities = result?.data?.communities || [];
      const threads = result?.data?.threads || [];

      return {
        success: true,
        data: {
          communities,
          threads,
          query: query.trim(),
          totalResults: communities.length + threads.length,
          meta: result?.meta || {},
        },
      };
    } catch (error) {
      const errorData = handleApiError(error);
throw errorData;
    }
  },
};

export default searchService;
